import {
  collection,
  DocumentData,
  onSnapshot,
  query,
} from "firebase/firestore";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { db } from "../../firebase/config";
import { Store } from "../type/Store";

interface StoreContextProps {
  setSelectedStoreId: (store: string | null) => void;
  selectedStoreId: string | null;
  clear: () => void;
  currentStore: Store | null | undefined;
  allStores: Store[];
  orders: DocumentData[];
  sessions: DocumentData[];
  isLoading: boolean;
}

const StoreContext = createContext<StoreContextProps>({
  setSelectedStoreId: () => {},
  selectedStoreId: null,
  clear: () => {},
  currentStore: null,
  allStores: [],
  orders: [],
  sessions: [],
  isLoading: true,
});

export const useStore = () => useContext(StoreContext);

type Props = {
  children: ReactNode;
};

const StoreContextProvider = ({ children }: Props) => {
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [orders, setOrders] = useState<DocumentData[]>([]);
  const [sessions, setSessions] = useState<DocumentData[]>([]);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load all stores (super admin sees all stores) - non-blocking
  useEffect(() => {
    const storesRef = collection(db, "stores");
    // Query without ordering to avoid index issues, we'll sort in memory
    const q = query(storesRef);

    // Set loading to false after initial render to allow components to render
    // The actual data will load asynchronously
    setIsLoading(true);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const stores: Store[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          stores.push({ 
            id: doc.id, 
            ...data,
            // Ensure default subscription plan fields
            subscriptionPlan: data.subscriptionPlan || "essential",
            subscriptionStatus: data.subscriptionStatus || "active",
            orderLimit: data.orderLimit ?? 100,
            stationStartTime: data.stationStartTime || "04:00",
          } as Store);
        });
        // Sort by created date if available (newest first), otherwise by name
        stores.sort((a, b) => {
          if (a.created && b.created) {
            return b.created.toMillis() - a.created.toMillis();
          }
          if (a.created && !b.created) return -1;
          if (!a.created && b.created) return 1;
          return (a.name || "").localeCompare(b.name || "");
        });
        setAllStores(stores);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error loading stores:", error);
        setAllStores([]);
        setIsLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const currentStore = useMemo(() => {
    if (!selectedStoreId) return null;
    return allStores.find((store) => store.id === selectedStoreId) || null;
  }, [allStores, selectedStoreId]);

  const clear = () => {
    setSelectedStoreId(null);
    setOrders([]);
    setSessions([]);
  };

  useEffect(() => {
    if (localStorage.getItem("selectedStore") && !selectedStoreId) {
      setSelectedStoreId(localStorage.getItem("selectedStore"));
    } else if (localStorage.getItem("selectedStore") !== selectedStoreId) {
      localStorage.setItem("selectedStore", selectedStoreId || "");
    }
  }, [selectedStoreId]);

  const value: StoreContextProps = {
    setSelectedStoreId,
    selectedStoreId,
    clear,
    currentStore,
    allStores,
    orders,
    sessions,
    isLoading,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export default StoreContextProvider;

